import { useParams, useSearchParams } from 'react-router-dom';
import { MessageSquare, ArrowRight } from 'lucide-react';
import { SearchParam } from '@common/ts/enums';
import { Button } from '@/components/ui/button';
import useResponsesPanelStore from '@/store/ResponsesPanelStore';
import useTranscriptPanelStore from '@/store/TranscriptPanelStore';
import useCategoryStore from '@/store/CategoryStore';
import useTranslation from '@/hooks/useTranslation';
import { useGetInterviewQuestionTranscriptsPaginated } from '@/reactQuery/get';
import type { CategoryApiResponse } from '@/types';
import { ResponseCard } from '../ResponsesPanel';

interface ResponsesZoneProps {
  questionId: string;
  categoryData?: CategoryApiResponse;
}

// How many response cards to surface inline. Kept small because the
// zone is a glanceable teaser — researchers who need filter/search/
// pagination click "Show all N responses" and the full
// ResponsesPanel takes over with all the rich controls.
const INLINE_PREVIEW_COUNT = 5;

/**
 * Inline zone surfacing the first few respondent answers directly in
 * the Questions main column (between StoryZone and EvidenceZone). It
 * exists to give voice-of-customer equal visual weight to AI-generated
 * insights without requiring a click.
 *
 * Deliberately read-only: no search, no category filter, no
 * pagination. Those capabilities live in the ResponsesPanel where
 * there is room for the controls and a close button. Putting filters
 * here caused a confusing UX — a search box that vanished when
 * results went to zero, no obvious way to reset, and filter state
 * that survived across question-navigations even though the zone's
 * other state reset.
 *
 * The zone follows the same translation language as the panel (via
 * the URL `language` param) so both views render in the same language.
 */
function ResponsesZone({ questionId, categoryData }: ResponsesZoneProps) {
  const { lang } = useTranslation();
  const { project_id: projectId } = useParams();
  const [searchParams] = useSearchParams();
  const filterId = searchParams.get(SearchParam.FILTER_ID);
  const translationLanguage = searchParams.get(SearchParam.TRANSLATION_LANGUAGE);
  const languageParam =
    !translationLanguage || translationLanguage === 'Original'
      ? null
      : translationLanguage;
  const categoryType = useCategoryStore((state) => state.categoryType);

  const { open: openResponsesPanel } = useResponsesPanelStore();
  const { open: openTranscriptPanel } = useTranscriptPanelStore();

  // Fetch exactly INLINE_PREVIEW_COUNT items. The server does the
  // ordering; we never pass search or category filters here — the
  // zone is an unfiltered preview by design.
  const { data: paginatedResponse } = useGetInterviewQuestionTranscriptsPaginated({
    filterId,
    categoryType,
    projectId,
    questionId,
    language: languageParam,
    page: 1,
    limit: INLINE_PREVIEW_COUNT,
    sortBy: 'respondent_index',
    sortDirection: 'asc',
  });

  const transcripts = paginatedResponse?.data ?? [];
  const totalResponses = paginatedResponse?.pagination?.totalItems ?? 0;

  const handleOpenTranscript = (conversationId: string) => {
    openTranscriptPanel(conversationId);
  };

  // Hide the zone when the question has no responses at all — a
  // "0 responses" section wastes real estate. Note we intentionally
  // only check the raw total, not a filtered total, because no
  // filtering is applied here.
  if (totalResponses === 0) return null;

  return (
    <section className="flex flex-col gap-3">
      {/* Zone header: title + count left, "Show all" CTA right */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-5 w-5 text-primary" />
          <h3 className="text-[15px] font-semibold text-neutral-900">
            {lang.get('msg.respondentAnswers') || 'Respondent answers'}
          </h3>
          <span className="text-sm font-normal text-neutral-400">
            ({totalResponses})
          </span>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => openResponsesPanel(questionId)}
          className="text-xs h-8 shrink-0"
        >
          {lang
            .get('msg.showAllNResponses')
            ?.replace('{n}', String(totalResponses)) ||
            `Show all ${totalResponses} responses`}
          <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
        </Button>
      </div>

      {/* Preview cards — first INLINE_PREVIEW_COUNT by respondent order */}
      <div className="flex flex-col gap-3">
        {transcripts.map((transcript, index) => (
          <ResponseCard
            key={transcript.conversation_id}
            transcript={transcript}
            index={index}
            categoryData={categoryData}
            questionId={questionId}
            projectId={projectId}
            onOpenTranscript={handleOpenTranscript}
          />
        ))}
      </div>

      {/* Footer CTA — opens full panel when there are more to see */}
      {totalResponses > transcripts.length && (
        <button
          type="button"
          onClick={() => openResponsesPanel(questionId)}
          className="self-start inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:text-darker-primary transition-colors"
        >
          {lang
            .get('msg.showAllNResponses')
            ?.replace('{n}', String(totalResponses)) ||
            `Show all ${totalResponses} responses`}
          <ArrowRight className="h-3.5 w-3.5" />
        </button>
      )}
    </section>
  );
}

export default ResponsesZone;
